import { Injectable, inject } from '@angular/core';
import { AuthenticationActions } from '@navfleet/client/authentication/domain/data-access';
import { AppActions } from '@navfleet/client/core/app';
import { ToasterService } from '@navfleet/client/shared/services/toast';
import { TenantFacade, TenantSignalRService } from '@navfleet/client/tenant/domain/data-access';
import { SignalRTenantSettingsUpdatedEvent, Tenant, TenantMethods } from '@navfleet/client/tenant/domain/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, tap } from 'rxjs';
import { catchError, concatMap, filter, map, take, takeUntil } from 'rxjs/operators';
import { TenantSettingsApi } from '../api/tenant-settings.api';
import { AppTenantSettingsActions, TenantSettingsActions } from './tenant-settings.actions';
import { TenantSettingsFacade } from './tenant-settings.facade';

@Injectable()
export class TenantSettingsEffects {
    private readonly actions$ = inject(Actions);
    private readonly api = inject(TenantSettingsApi);
    private readonly facade = inject(TenantSettingsFacade);
    private readonly tenantFacade = inject(TenantFacade);
    private readonly tenantSignalRService = inject(TenantSignalRService);
    private readonly toasterService = inject(ToasterService);

    public readonly initLoadTenantSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.loginSuccess),
            map(() => TenantSettingsActions.loadTenantSettings())
        )
    );

    public readonly loadTenantSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TenantSettingsActions.loadTenantSettings),
            concatMap(() =>
                this.api.getTenantSettings().pipe(
                    map((settings) => TenantSettingsActions.loadTenantSettingsSuccess({ settings })),
                    catchError((error) => of(TenantSettingsActions.loadTenantSettingsFailure({ error }))),
                    takeUntil(this.actions$.pipe(ofType(AppActions.applicationClosed)))
                )
            )
        )
    );

    public readonly tenantSettingsLoaded$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TenantSettingsActions.loadTenantSettingsSuccess),
            map(() => AppTenantSettingsActions.tenantSettingsLoaded())
        )
    );

    public readonly updateTenantSetting$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TenantSettingsActions.updateTenantSetting),
            filter(({ setting }) => !!setting.canSet),
            concatMap(({ setting }) =>
                this.api.updateTenantSetting(setting).pipe(
                    map(() => {
                        this.toasterService.success('COMMON.SAVED_CHANGES');
                        return TenantSettingsActions.updateTenantSettingSuccess();
                    }),
                    catchError((error) => {
                        this.toasterService.error(error?.translationCode ?? error?.message);
                        return of(TenantSettingsActions.updateTenantSettingFailure({ error }));
                    }),
                    takeUntil(this.actions$.pipe(ofType(AppActions.applicationClosed)))
                )
            )
        )
    );

    public readonly tenantSettingsSignalR$ = createEffect(
        () =>
            this.tenantSignalRService.on$(TenantMethods.TenantSettingsUpdated).pipe(tap((event) => this.tenantSettingsUpdated(event))),
        {
            dispatch: false,
        }
    );

    private tenantSettingsUpdated(event: SignalRTenantSettingsUpdatedEvent) {
        this.tenantFacade
            .getSelectedTenant()
            .pipe(take(1))
            .subscribe((tenant: Tenant) => {
                const isLoggedOnTenant = tenant.id === event?.tenantId;
                if (isLoggedOnTenant) {
                    this.facade.loadTenantSettings();
                }
            });
    }
}
